/**
 * Drag-to-move for a fixed-position floating widget.
 *
 * The element keeps whatever position its CSS anchors it to until the first drag. From then on it
 * is placed by inline `left`/`top` in viewport pixels, and the spot the user picked is remembered
 * in this browser — a widget that jumped back to its corner on every navigation would not be worth
 * moving in the first place.
 *
 * Two details the caller must wire up, both returned from here:
 * - `onClickCapture` swallows the click that ends a drag, so releasing the pointer does not also
 *   activate the widget. It runs in the capture phase and calls `stopPropagation`, which is what
 *   keeps a Radix trigger on the same element from toggling.
 * - the element needs `touch-action: none` (Tailwind `touch-none`), or a touch drag scrolls the
 *   page instead of moving the widget.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

/** Gap kept between the widget and the viewport edge when clamping. */
const EDGE_MARGIN = 8;

/**
 * A drag only begins past this many pixels of travel. Below it the gesture is still a click, so a
 * slightly shaky tap opens the widget rather than nudging it.
 */
const DRAG_THRESHOLD = 4;

interface Position {
  /** Distance from the left edge of the viewport, in CSS pixels. */
  x: number;
  /** Distance from the top edge of the viewport, in CSS pixels. */
  y: number;
}

interface DragSession {
  pointerId: number;
  /** Where inside the element the pointer grabbed it — keeps the widget from jumping on grab. */
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  startX: number;
  startY: number;
  /** True once the pointer has travelled past `DRAG_THRESHOLD`. */
  moved: boolean;
}

export interface DraggableWidget<T extends HTMLElement> {
  /** Attach to the element that should move. */
  ref: RefObject<T | null>;
  /** True only while an actual drag is in progress — a plain click never sets it. */
  isDragging: boolean;
  /** Undefined while the widget still sits at its CSS-anchored default. */
  style: CSSProperties | undefined;
  onPointerDown: (event: ReactPointerEvent<T>) => void;
  onPointerMove: (event: ReactPointerEvent<T>) => void;
  onPointerUp: (event: ReactPointerEvent<T>) => void;
  onPointerCancel: (event: ReactPointerEvent<T>) => void;
  onClickCapture: (event: ReactMouseEvent<T>) => void;
}

/** Keeps the whole widget inside the viewport, whatever screen it is opened on next. */
function clampToViewport(position: Position, width: number, height: number): Position {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - width - EDGE_MARGIN);
  const maxY = Math.max(EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN);

  return {
    x: Math.min(Math.max(position.x, EDGE_MARGIN), maxX),
    y: Math.min(Math.max(position.y, EDGE_MARGIN), maxY),
  };
}

function readStoredPosition(storageKey: string): Position | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<Position> | null;
    const x = parsed?.x;
    const y = parsed?.y;
    if (typeof x !== "number" || typeof y !== "number" || !Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }

    return { x, y };
  } catch {
    // Corrupt value, or storage blocked (Safari private mode) — fall back to the default corner.
    return null;
  }
}

function writeStoredPosition(storageKey: string, position: Position): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(position));
  } catch {
    // Storage being unavailable must not break dragging; the position is simply not remembered.
  }
}

export function useDraggableWidget<T extends HTMLElement>(
  storageKey: string,
): DraggableWidget<T> {
  const ref = useRef<T | null>(null);
  const [position, setPosition] = useState<Position | null>(() =>
    readStoredPosition(storageKey),
  );
  const [isDragging, setIsDragging] = useState(false);

  const positionRef = useRef(position);
  const dragRef = useRef<DragSession | null>(null);
  /** Set when a drag ends, so the click it produces is discarded instead of opening the widget. */
  const suppressClickRef = useRef(false);

  const applyPosition = useCallback((next: Position) => {
    positionRef.current = next;
    setPosition(next);
  }, []);

  // A position stored on a wide screen can land off-canvas on a narrow one, so re-clamp against
  // the real element size before the first paint.
  useLayoutEffect(() => {
    const element = ref.current;
    const current = positionRef.current;
    if (!element || !current) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const next = clampToViewport(current, rect.width, rect.height);
    if (next.x !== current.x || next.y !== current.y) {
      applyPosition(next);
      writeStoredPosition(storageKey, next);
    }
  }, [applyPosition, storageKey]);

  useEffect(() => {
    const handleResize = () => {
      const element = ref.current;
      const current = positionRef.current;
      if (!element || !current) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const next = clampToViewport(current, rect.width, rect.height);
      if (next.x !== current.x || next.y !== current.y) {
        applyPosition(next);
        writeStoredPosition(storageKey, next);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [applyPosition, storageKey]);

  const handlePointerDown = useCallback((event: ReactPointerEvent<T>) => {
    // Secondary mouse buttons open the context menu; they must not start a drag.
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    // A drag released outside the element fires its click on an ancestor, so our capture handler
    // never sees it. Clearing the flag here bounds that to the drag it belongs to.
    suppressClickRef.current = false;

    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();

    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };

    // Keeps the moves coming even when the pointer outruns the element. Optional-called because
    // jsdom (and very old WebViews) ship no Pointer Capture — there the drag simply stops
    // updating once the pointer leaves the element instead of throwing.
    element.setPointerCapture?.(event.pointerId);
  }, []);

  /** Closes out a drag session: shared by pointerup, pointercancel and a lost pointer capture. */
  const finishDrag = useCallback(
    (drag: DragSession) => {
      dragRef.current = null;

      if (!drag.moved) {
        return;
      }

      suppressClickRef.current = true;
      setIsDragging(false);

      if (positionRef.current) {
        // Written once, at the end — a write per pointermove would hit storage ~60 times a second.
        writeStoredPosition(storageKey, positionRef.current);
      }
    },
    [storageKey],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<T>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      // No button held any more: the pointerup went somewhere else (a lost capture, a browser
      // gesture). Close the session here, or the widget would keep following the free pointer.
      if (event.buttons === 0) {
        finishDrag(drag);
        return;
      }

      if (!drag.moved) {
        const travelled =
          Math.abs(event.clientX - drag.startX) >= DRAG_THRESHOLD ||
          Math.abs(event.clientY - drag.startY) >= DRAG_THRESHOLD;
        if (!travelled) {
          return;
        }

        drag.moved = true;
        setIsDragging(true);
      }

      applyPosition(
        clampToViewport(
          { x: event.clientX - drag.offsetX, y: event.clientY - drag.offsetY },
          drag.width,
          drag.height,
        ),
      );
    },
    [applyPosition, finishDrag],
  );

  const endDrag = useCallback(
    (event: ReactPointerEvent<T>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      finishDrag(drag);
    },
    [finishDrag],
  );

  const handleClickCapture = useCallback((event: ReactMouseEvent<T>) => {
    if (!suppressClickRef.current) {
      return;
    }

    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    ref,
    isDragging,
    style: position
      ? { left: position.x, top: position.y, right: "auto", bottom: "auto" }
      : undefined,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onClickCapture: handleClickCapture,
  };
}
